import { computed } from 'vue';
import $ from 'jquery';
import { createPopper } from '@popperjs/core';
import _ from 'lodash';
import { useI18n } from '@/composables/use-i18n';
import { useRoute, useRouter } from 'vue-router/composables';
import scrollIntoView from 'scroll-into-view-if-needed';
import {
    COMPARE_ROUTE_PATH,
    useSimilarParaScroll,
} from '@/composables/use-similar-para-scroll';
import { useCompareWording } from '@/composables/use-compare-wording';
import { useDualViewStates } from '@/composables/use-dual-view-states';

const SIMILAR_HIT_CLASS_NAME = 'patsnap-compare-hit';
const SIMILAR_HIT_CLASS_ACTIVE_NAME = 'patsnap-compare-hit--active';
const SIMILAR_HIT_PARAGRAPH_ATTR = 'data-similar-paragraph';
const SIMILAR_HIT_PARAGRAPH_PART_ATTR = 'part';

const SIMILAR_WORDING_ID_ATTR = 'data-wording-id';

const SIMILAR_TAG_ONLY_KEY = 'data-similar-tag-only-key';
const SIMILAR_TAG_WRAPPER_CLASS_NAME = 'similar-para-wrapper';
const SIMILAR_TAG_CLASS_NAME = 'similar-para-tag';
const SIMILAR_TAG_CLASS_HOVER_NAME = 'similar-para-tag--hover';
const SIMILAR_TAG_CLASS_ACTIVE_NAME = 'similar-para-tag--active';
const SIMILAR_WORDING_TAG_CLASS_NAME = 'similar-para-wording-tag';

const SIMILAR_TAG_LABEL_CLASS_NAME = 'similar-para-tag__label';
const SIMILAR_TAG_NUM_CLASS_NAME = 'similar-para-tag__num';
// 用于排序
const SIMILAR_TAG_NUM_ATTR = 'data-similar-tag-num';

interface SimilarParagraphPositionParams {
    side: string;
    content: HTMLElement;
}

interface SimilarParagraphWordingPositionParams {
    side: string;
    content: HTMLElement;
    similarNum: string;
    uniqueId: string;
}

interface SimilarTagParams {
    side: string;
    content: HTMLElement;
    num: string;
    part: string;
}

export const useSimilarParagraph = () => {
    const { t } = useI18n();
    const route = useRoute();
    const router = useRouter();
    const { curSimilarParaNum, changeSimilarParaNum, changeScrollImmediately, getCurScrollInfo } = useSimilarParaScroll();
    const { compareWordingParaNumMap } = useCompareWording();
    const { leftRef, rightRef } = useDualViewStates();

    const leftName = computed(() => {
        return route.params.left as string;
    });

    // 对比栏name
    const rightName = computed(() => {
        return route.params.right as string;
    });

    // 用于定位
    // let currentSide = '';
    let neighborSide = '';

    const setState = (side:string) => {
        // currentSide = side;
        neighborSide = side === 'left' ? 'right' : 'left';
    };

    const jumpBySelector = (target:Element) => {
        if (target) {
            scrollIntoView(target, {
                scrollMode: 'if-needed',
                inline: 'center',
                block: 'center',
                behavior: 'smooth',
            });
            window.dispatchEvent(new Event('resize'));
        }
    };

    const activeSimilarTag = (num:string, wrapper:HTMLElement) => {
        //清空其他同类激活状态
        $(wrapper).find(`.${SIMILAR_TAG_CLASS_NAME}`).removeClass(SIMILAR_TAG_CLASS_ACTIVE_NAME);
        $(wrapper).find(`.${SIMILAR_HIT_CLASS_NAME}`).removeClass(SIMILAR_HIT_CLASS_ACTIVE_NAME);
        $(wrapper).find(`.${SIMILAR_TAG_LABEL_CLASS_NAME}`).remove();

        const label = $('<span>').text(t('patent-view.compareView.similarPara')).addClass(SIMILAR_TAG_LABEL_CLASS_NAME);
        $(wrapper).find(`.${SIMILAR_TAG_NUM_CLASS_NAME}_${num}`).parents(`.${SIMILAR_TAG_CLASS_NAME}`).addClass(SIMILAR_TAG_CLASS_ACTIVE_NAME).prepend(label);
        $(wrapper).find(`[${SIMILAR_HIT_PARAGRAPH_ATTR}=${num}]`).addClass(SIMILAR_HIT_CLASS_ACTIVE_NAME);

    };

    /*
    * num 相似段落编号
    * index 相似段落index，从1开始
    * target 左右栏位
    * name 滚动目标的route name
    */
    const scrollToSimilarPara = ({ num, target, index, name }: {
        num: string;
        target: string;
        index: number;
        name: string;
    }) => {
        // 获取元素并滚动
        const targetName = target === 'left' ? leftName.value : rightName.value;
        if (targetName === name) {
            const wrapper = (target === 'left' ? leftRef.value : rightRef.value)?.$el;
            let selector;
            if (name === COMPARE_ROUTE_PATH) {
                selector = `.match-desc-item__${num}`;
            } else {
                activeSimilarTag(num, wrapper as HTMLElement);
                // selector = `[${SIMILAR_HIT_PARAGRAPH_ATTR}=${num}]`;
                // 根据partId和similar para定位，不要根据段落了，顺序可能会乱掉
                selector = `[${SIMILAR_HIT_PARAGRAPH_ATTR}=${num}][${SIMILAR_HIT_PARAGRAPH_PART_ATTR}=${index}]`;
            }
            const ele = $(wrapper).find(selector).get(0);
            jumpBySelector(ele);
        } else {
            changeScrollImmediately({
                side: target,
                status: true,
            });
            router.push({
                query: route.query,
                params: {
                    ...route.params,
                    [target]: name,
                },
            });
        }
    };

    const createTagPopper = (target:Element, content: HTMLElement) => {
        createPopper(target, content, {
            placement: 'top-start',
            strategy: 'absolute',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, -2],
                    },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: [],
                    },
                },
                {
                    name: 'eventListeners',
                    options: {
                        scroll: false,
                        resize: true,
                    },
                },
            ],
        });
    };

    const bindEvent = (content:HTMLElement) => {
        // hover时一些样式的修改
        $(content).on('mouseenter', `.${SIMILAR_HIT_CLASS_NAME}`, (e) => {
            const target = e.currentTarget;
            const currentParaNum = $(target).attr(SIMILAR_HIT_PARAGRAPH_ATTR);
            const currentParaNumPart = $(target).attr(SIMILAR_HIT_PARAGRAPH_PART_ATTR);
            const currentPara = $(content).find(`[${SIMILAR_HIT_PARAGRAPH_ATTR}=${currentParaNum}][${SIMILAR_HIT_PARAGRAPH_PART_ATTR}=${currentParaNumPart}]`);

            // currentPara.addClass(SIMILAR_HIT_CLASS_HOVER_NAME);
            currentPara.find(`.${SIMILAR_TAG_CLASS_NAME}`).addClass(SIMILAR_TAG_CLASS_HOVER_NAME);
        });

        $(content).on('mouseleave', `.${SIMILAR_HIT_CLASS_NAME}`, (e) => {
            const target = e.currentTarget;
            const currentParaNum = $(target).attr(SIMILAR_HIT_PARAGRAPH_ATTR);
            const currentParaNumPart = $(target).attr(SIMILAR_HIT_PARAGRAPH_PART_ATTR);
            const currentPara = $(content).find(`[${SIMILAR_HIT_PARAGRAPH_ATTR}=${currentParaNum}][${SIMILAR_HIT_PARAGRAPH_PART_ATTR}=${currentParaNumPart}]`);

            // currentPara.removeClass(SIMILAR_HIT_CLASS_HOVER_NAME);
            currentPara.find(`.${SIMILAR_TAG_CLASS_NAME}`).removeClass(SIMILAR_TAG_CLASS_HOVER_NAME);
        });

        $(content).on('click', `.${SIMILAR_TAG_CLASS_NAME}`, (e) => {
            const target = e.currentTarget;
            const similarNum = $(target).find(`.${SIMILAR_TAG_NUM_CLASS_NAME}`).text();
            activeSimilarTag(similarNum, content);

            /*
            * 反方向的钟
            * 如果是这种方式去调用，实际上，这里调用的是neighborSideRef组件，对于下面的方法来说，传进去的应该是当前的ref元素
            * */
            /*if (neighborSideRef.scrollToSimilarFirstPara) {
                neighborSideRef.scrollToSimilarFirstPara(similarNum, neighborSideRef)
            }*/

            // const neighborSidePath = _.get(route.params, neighborSide);
            // 这里的点击是跳转到命中的第一条
            // 主题栏点击滚动，取curSimilarParaScrollInfo的值判断位置。
            changeSimilarParaNum(similarNum);
            /* const params = currentSide === 'left' ? {
                num: similarNum,
                target: neighborSide,
                index: curTargetPatentScrollInfo.value.index,
                name: curTargetPatentScrollInfo.value.name
            } : {
                num: similarNum,
                target: neighborSide,
                index: 1,
                name: CLMS_ROUTE_PATH,
            } */
            const params = getCurScrollInfo(neighborSide);
            scrollToSimilarPara(params);
        });
    };

    // 修改wording dom属性
    const setCompareTagDom = ({
        side,
        content,
        num,
        part,
    }:SimilarTagParams) => {
        // 相同段落编号，且part一致，则为一段，只打一个段落编号的tag。
        const similarTagOnlyKey = `${num}_${part}`;
        // 如果已经有符合规则的tag了，则不继续添加tag
        if ($(content).find(`[${SIMILAR_TAG_ONLY_KEY}=${similarTagOnlyKey}]`).get(0)) {
            return;
        }
        const similarTagDom = $('<div>').addClass(`${SIMILAR_TAG_CLASS_NAME} ${SIMILAR_TAG_CLASS_NAME}__${side}`).attr(SIMILAR_TAG_ONLY_KEY, similarTagOnlyKey);
        const numDom = $('<span>').text(num).addClass(`${SIMILAR_TAG_NUM_CLASS_NAME} ${SIMILAR_TAG_NUM_CLASS_NAME}_${num}`);
        similarTagDom.append(numDom);
        similarTagDom.attr('title', t('patent-view.compareView.clickToSimilar'));
        return similarTagDom.get(0);
    };

    const setSimilarWordingTagPosition = ({
        side,
        content,
        similarNum,
        uniqueId,
    }:SimilarParagraphWordingPositionParams) => {
        const paras = $(`[${SIMILAR_WORDING_ID_ATTR}=${uniqueId}]`);
        // 同一个划段部分一定是同一个part
        const part = '1';
        _.forEach(paras, para => {
            // 添加跳转相关的关键属性
            $(para).attr(SIMILAR_HIT_PARAGRAPH_ATTR, similarNum).attr(SIMILAR_HIT_PARAGRAPH_PART_ATTR, part);
            $(para).addClass(SIMILAR_HIT_CLASS_NAME);
            const similarTagDom = setCompareTagDom({
                side,
                content,
                part,
                num: similarNum,
            });

            if (similarTagDom) {
                $(similarTagDom).addClass(SIMILAR_WORDING_TAG_CLASS_NAME);
                const similarTaGWrapperDom = $('<div>').addClass(SIMILAR_TAG_WRAPPER_CLASS_NAME);
                $(para).append(similarTaGWrapperDom);
                $(similarTaGWrapperDom).append(similarTagDom);
                createTagPopper(para, similarTaGWrapperDom.get(0));
            }
        });
    };

    const setSimilarParaTagPosition = ({
        side,
        content,
    }:SimilarParagraphPositionParams) => {
        setState(side);
        // 接口返回的标签
        const allParas = content.querySelectorAll(`.${SIMILAR_HIT_CLASS_NAME}`);
        _.forEach(allParas, para => {
            // 获取段落编号
            const currentParaNum = $(para).attr(SIMILAR_HIT_PARAGRAPH_ATTR);
            const currentParaNumPart = $(para).attr(SIMILAR_HIT_PARAGRAPH_PART_ATTR);
            // 相同段落编号，且part一致，则为一段，只打一个段落编号的tag。
            const similarTagOnlyKey = `${currentParaNum}_${currentParaNumPart}`;
            // 如果已经有符合规则的tag了，则不继续添加tag
            if ($(content).find(`[${SIMILAR_TAG_ONLY_KEY}=${similarTagOnlyKey}]`).get(0)) {
                return;
            }

            const similarTagDom = $('<div>').addClass(`${SIMILAR_TAG_CLASS_NAME} ${SIMILAR_TAG_CLASS_NAME}__${side}`).attr(SIMILAR_TAG_ONLY_KEY, similarTagOnlyKey);
            const num = $('<span>').text(currentParaNum).addClass(`${SIMILAR_TAG_NUM_CLASS_NAME} ${SIMILAR_TAG_NUM_CLASS_NAME}_${currentParaNum}`);
            similarTagDom.append(num);
            similarTagDom.attr('title', t('patent-view.compareView.clickToSimilar'));
            similarTagDom.attr(SIMILAR_TAG_NUM_ATTR, currentParaNum);

            // 有可能是标签嵌套，需要一直循环找到最外层的命中元素。
            let ancestorsParas = $(para).closest(`.${SIMILAR_HIT_CLASS_NAME}`).get(0);

            // 是否查找到最外层
            let isOutermost = !ancestorsParas;
            while (!isOutermost) {
                const _ancestorsParas = $(ancestorsParas).parents(`.${SIMILAR_HIT_CLASS_NAME}`).get(0);
                if (_ancestorsParas) {
                    ancestorsParas = _ancestorsParas;
                } else {
                    isOutermost = true;
                }
            }
            // 为什么不挂组件而是直接JQuery呢？因为多个实例不好控制
            let similarTaGWrapperDom = $(ancestorsParas).find(`.${SIMILAR_TAG_WRAPPER_CLASS_NAME}`).get(0);
            if (similarTaGWrapperDom) {
                // 在这里面进行排序
                const allTag = similarTaGWrapperDom.querySelectorAll(`.${SIMILAR_TAG_CLASS_NAME}`);
                const allTagDomList = Array.from(allTag);
                allTagDomList.push(similarTagDom.get(0));
                const sortedTag = allTagDomList.sort((a, b) => {
                    const aNum = parseInt($(a).attr(SIMILAR_TAG_NUM_ATTR), 0);
                    const bNum = parseInt($(b).attr(SIMILAR_TAG_NUM_ATTR), 0);
                    return aNum - bNum;
                });
                $(similarTaGWrapperDom).empty().append(sortedTag);
            } else {
                similarTaGWrapperDom = $('<div>').addClass(SIMILAR_TAG_WRAPPER_CLASS_NAME);
                $(para).append(similarTaGWrapperDom);
                $(similarTaGWrapperDom).append(similarTagDom);

                /*使用popperJs定位元素是为了计算水平方向的碰撞逻辑
                * 如果多个相似段的tag都定位在一行，切文段起始位于右侧，这样会有以下一个情景：
                * 如果不计算碰撞逻辑，tag的开始完全根据相似语段的开始，则可能tag会超出容器范围
                * 为了避免这种问题的出现，使用popperJs计算碰撞逻辑，如果位置不够，tag自动往左方向推
                */
                createTagPopper(para, similarTaGWrapperDom.get(0));
            }
        });

        bindEvent(content);
        if (curSimilarParaNum.value) {
            activeSimilarTag(curSimilarParaNum.value, content);
        }
    };

    const revertSimilarWordingTagPosition = ({
        side,
        content,
    }:SimilarParagraphPositionParams) => {
        // 划段标签
        if (!_.isEmpty(compareWordingParaNumMap.value)) {
            for(const key in compareWordingParaNumMap.value) {
                const similarNum = key;
                const uniqueId = compareWordingParaNumMap.value[key].uniqueId;
                setSimilarWordingTagPosition({
                    side,
                    content,
                    similarNum,
                    uniqueId,
                });
            }

            // 如果该tag命中，需要手动active一下
            if (compareWordingParaNumMap.value[curSimilarParaNum.value]) {
                activeSimilarTag(curSimilarParaNum.value, content);
            }
        }
    };

    return {
        /*
        * 相似段落tag标签定位
        * */
        setSimilarParaTagPosition,
        /*
        * 划段的tag标签定位
        * */
        setSimilarWordingTagPosition,
        /*
        * 还原划段的tag
        */
        revertSimilarWordingTagPosition,
        /*
        * 滚动到tag对应的相似段落
        * */
        scrollToSimilarPara,
    };
};
